import {
  TLocationDetails,
  TMachineDescriptionDTO,
  TMinimalEmployeeDetailsV2,
  TRESPONSE_GET_ALL_EMPLOYEE_TICKETS,
  TTicketInfoDTO,
} from '@monorepo/types';
import { TAccordionActionInfo, TAccordionTableVM } from '../../model/accordion-table.types';
import { TICKET_LIST_HELPER } from './ticket-list-helper';
import { compact } from 'lodash';
import { ACCORDION_TABLE_MODIFIER } from '../../model/accordion-table.modifier';
import { MOBILE_SCREEN_UTILS } from '../../../utils/mobile-screen.utils';

// NOTE[Ramya]: add actions based on feature codes after v1
// NOTE: The order of actions in actionIds array is same as it's displayed on the UI.
const TICKET_ACTIONS_CONF = {
  actions: [
    {
      status: 'REPORTED',
      actionIds: ['RE_ASSIGN_TICKET', 'START_REPAIR', 'UPDATE_MACHINE_ISSUE'],
    },
    {
      status: 'PENDING_APPROVAL',
      actionIds: ['APPROVE_TICKET', 'RAISE_REQUEST', 'REISSUE_TICKET', 'VIEW_REQUEST_DETAILS'],
    },
    {
      status: 'PENDING_REQUEST',
      actionIds: [
        'RAISE_REQUEST',
        'VIEW_REQUEST_DETAILS',
        'MARK_AS_COMPLETED',
        'RE_ASSIGN_TICKET',
        'UPDATE_MACHINE_ISSUE',
        'REPLACE_MACHINE',
      ],
    },

    {
      status: 'IN_REPAIR',
      actionIds: [
        'MARK_AS_COMPLETED',
        'RAISE_REQUEST',
        'RE_ASSIGN_TICKET',
        'UPDATE_MACHINE_ISSUE',
        'REPLACE_MACHINE',
        'VIEW_REQUEST_DETAILS',
      ],
    },
    {
      status: 'APPROVED',
      actionIds: ['VIEW_REQUEST_DETAILS'],
    },
  ],
};

function _getactionsInfoByStatus(selectedTicketStatus: string, actionConf: TAccordionActionInfo[]) {
  const actionsFilteredByStatus = TICKET_ACTIONS_CONF.actions.find((action) => action.status === selectedTicketStatus);
  if (actionsFilteredByStatus) {
    const actionsInfoByStatus = actionsFilteredByStatus.actionIds
      .map((actionId) => actionConf.find((actionInfo) => actionInfo.actionId === actionId))
      .filter((actionInfo): actionInfo is TAccordionActionInfo => actionInfo !== undefined);

    return actionsInfoByStatus;
  } else {
    return [];
  }
}

function getEmployeeDisplayName(employee: TMinimalEmployeeDetailsV2): string {
  return `${employee.employeeName} (${employee.factoryId})`;
}

const MOBILE_HEADERS = [
  { label: 'tm_container_labels.Date&Time', colSize: 7 },
  { label: 'tm_container_labels.MachineIssueHeader', colSize: 9 },
  { label: 'tm_container_labels.LocationHeader', colSize: 7 },
];

// colSizes sum to 23; chevron takes the remaining 1 of the 24-column Ionic grid
const WEB_HEADERS = [
  { label: 'tm_container_labels.Date&Time', colSize: 4 },
  { label: 'tm_container_labels.MachineIssueHeader', colSize: 4 },
  { label: 'tm_container_labels.LocationHeader', colSize: 3 },
  { label: 'tm_container_labels.AttendingTime', colSize: 3 },
  { label: 'tm_container_labels.RepairedTime', colSize: 3 },
  { label: 'tm_container_labels.ApprovalTime', colSize: 3 },
  { label: 'tm_container_labels.DownTime', colSize: 3 },
];

export const MACHINE_ISSUE_ACCORDION_TABLE_MODEL = {
  createModel(
    tickets: TRESPONSE_GET_ALL_EMPLOYEE_TICKETS['allTickets'],
    actionConf: TAccordionActionInfo[],
    selectedTicketStatus?: string,
    redirectedTicketId?: number,
  ): TAccordionTableVM {
    const isMobileScreen = MOBILE_SCREEN_UTILS.isMobileScreen();
    return ACCORDION_TABLE_MODIFIER.createModel(
      isMobileScreen ? MOBILE_HEADERS : WEB_HEADERS,
      tickets.map((ticket) => {
        const timeRowItems = isMobileScreen
          ? []
          : [
              {
                type: 'DURATION' as const,
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.grabTimeInMins),
              },
              {
                type: 'DURATION' as const,
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.repairedTimeInMins),
              },
              {
                type: 'DURATION' as const,
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.approvalTimeInMinutes),
              },
              {
                type: 'DURATION' as const,
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.downTimeInMins),
              },
            ];

        const timeModeInfoItems = isMobileScreen
          ? [
              {
                size: 12 as const,
                label: 'tm_container_labels.AttendingTime',
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.grabTimeInMins),
                type: 'DURATION' as const,
              },
              {
                size: 12 as const,
                label: 'tm_container_labels.RepairedTime',
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.repairedTimeInMins),
                type: 'DURATION' as const,
              },
              {
                size: 12 as const,
                label: 'tm_container_labels.ApprovalTime',
                values: TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.approvalTimeInMinutes),
                type: 'DURATION' as const,
              },
            ]
          : [];

        return {
          id: ticket.ticketInfo.ticketId,
          tag: compact([
            ticket.machineDescription.factorySerialNumber
              ? {
                  displayName: ticket.machineDescription.factorySerialNumber,
                  class: 'accordian-table__tag--data',
                  frequency: 1,
                }
              : undefined,
            ticket.ticketInfo.breakdownReasonDetails?.issueCategoryOpt
              ? {
                  displayName: `${ticket.ticketInfo.breakdownReasonDetails?.issueCategoryOpt}`,
                  class: 'accordian-table__tag',
                  frequency: 1,
                }
              : undefined,
            ticket.ticketInfo.metadata.reissuedCount > 0
              ? {
                  displayName: 'tm_container_labels.Re-issued',
                  frequency: ticket.ticketInfo.metadata.reissuedCount,
                  class: 'accordian-table__tag--red',
                }
              : undefined,
            ticket.ticketInfo.metadata.isReassigned
              ? {
                  displayName: 'tm_container_labels.Re-Assigned',
                  frequency: 1,
                  class: 'accordian-table__tag--blue',
                }
              : undefined,
          ]),
          isMinimized: redirectedTicketId ? !(redirectedTicketId === ticket.ticketInfo.ticketId) : true,
          rowItems: [
            {
              type: 'DATE_AND_TIME' as const,
              timestamp: ticket.ticketInfo.createdAt,
            },
            { type: 'TEXT' as const, value: ticket.ticketInfo.breakdownReasonDetails?.reason || '--' },
            { type: 'TEXT' as const, value: ticket.ticketInfo.ticketLocationDetails.locationCode },
            ...timeRowItems,
          ],
          moreInfo: [
            {
              items: [
                {
                  size: 12,
                  label: 'tm_container_labels.MachineSerialID',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.factorySerialNumber]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Assignee',
                  values: ticket.ticketInfo.mechanicDetails
                    ? TICKET_LIST_HELPER.mapToValuesList([getEmployeeDisplayName(ticket.ticketInfo.mechanicDetails)])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.BreakdownReason',
                  values: ticket.ticketInfo.breakdownReasonDetails
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.breakdownReasonDetails?.reason])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.MachineType',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineType]),
                },
                ...timeModeInfoItems,
                {
                  size: 12,
                  label: 'tm_container_labels.DownTime',
                  values:
                    ticket.ticketInfo.downTimeInMins !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.downTimeInMins)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Category',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineCategory]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Sub-Category',
                  values: ticket.machineDescription.machineSubCategory
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineSubCategory])
                    : [],
                },
                {
                  size: 12,
                  label: 'Location Code',
                  values: ticket.ticketInfo.ticketLocationDetails.locationCode
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.ticketLocationDetails.locationCode])
                    : [],
                },
              ],
            },
          ],
          actions:
            selectedTicketStatus && tickets.length ? _getactionsInfoByStatus(selectedTicketStatus, actionConf) : [],
        };
      }),
      {
        showExpand: true,
      },
    );
  },
};

export const MACHINE_ISSUE_TICKET_DETAILS_MODEL = {
  createModel(
    tickets: {
      machineDescription: TMachineDescriptionDTO;
      ticketInfo: TTicketInfoDTO;
    }[],
    actionConf: TAccordionActionInfo[],
    selectedTicketStatus?: string,
    locationDisplayCallBack?: (location: TLocationDetails['location']) => string[],
    redirectedTicketId?: number,
  ): TAccordionTableVM {
    return ACCORDION_TABLE_MODIFIER.createModel(
      [
        { label: 'tm_container_labels.Date&Time', colSize: 7 },
        { label: 'tm_container_labels.MachineIssueHeader', colSize: 9 },
        { label: 'tm_container_labels.LocationHeader', colSize: 7 },
      ],
      tickets.map((ticket) => {
        return {
          id: ticket.ticketInfo.ticketId,
          tag: compact([
            ticket.ticketInfo.metadata.reissuedCount > 0
              ? {
                  displayName: 'tm_container_labels.Re-issued',
                  frequency: ticket.ticketInfo.metadata.reissuedCount,
                  class: 'ticket-details__tag--red',
                }
              : undefined,
            ticket.ticketInfo.reassignedByOpt
              ? {
                  displayName: 'tm_container_labels.Re-Assigned',
                  frequency: 1,
                  class: 'ticket-details__tag--blue',
                }
              : undefined,
          ]),
          isMinimized: redirectedTicketId ? !(redirectedTicketId === ticket.ticketInfo.ticketId) : true,
          rowItems: [
            {
              type: 'DATE_AND_TIME',
              timestamp: ticket.ticketInfo.createdAt,
            },
            { type: 'TEXT', value: ticket.ticketInfo.breakdownReasonDetails?.reason || '--' },
            { type: 'TEXT', value: ticket.ticketInfo.ticketLocationDetails.locationCode },
          ],
          moreInfo: [
            {
              items: [
                {
                  size: 12,
                  label: 'tm_container_labels.MachineSerialID',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.factorySerialNumber]),
                },
                {
                  size: 12,
                  label: 'Location Code',
                  values: ticket.ticketInfo.ticketLocationDetails.locationCode
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.ticketLocationDetails.locationCode])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Solution',
                  values: ticket.ticketInfo.solutionOpt
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.solutionOpt])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Assignee',
                  values: ticket.ticketInfo.mechanicDetails
                    ? TICKET_LIST_HELPER.mapToValuesList([getEmployeeDisplayName(ticket.ticketInfo.mechanicDetails)])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.RaisedBy',
                  values: TICKET_LIST_HELPER.mapToValuesList([getEmployeeDisplayName(ticket.ticketInfo.raisedBy)]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.ApprovedBy',
                  values:
                    ticket.ticketInfo.approvedByOpt !== undefined
                      ? TICKET_LIST_HELPER.mapToValuesList([getEmployeeDisplayName(ticket.ticketInfo.approvedByOpt)])
                      : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.DownTime',
                  values:
                    ticket.ticketInfo.downTimeInMins !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.downTimeInMins)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.MachineType',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineType]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.ResponseTime',
                  values:
                    ticket.ticketInfo.grabTimeInMins !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.grabTimeInMins)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Category',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineCategory]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.RepairTime',
                  values:
                    ticket.ticketInfo.repairedTimeInMins !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.repairedTimeInMins)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.SpApprovalTime',
                  values:
                    ticket.ticketInfo.sparePartRequisitionTimeInMins !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.sparePartRequisitionTimeInMins)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Sub-Category',
                  values: ticket.machineDescription.machineSubCategory
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineSubCategory])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.EndTime',
                  values: ticket.ticketInfo.endTimeInMins
                    ? TICKET_LIST_HELPER.mapToValuesList([
                        TICKET_LIST_HELPER.formatTimeStamp(ticket.ticketInfo.endTimeInMins),
                      ])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.TicketID',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.ticketId.toString()]),
                },
                {
                  size: 12,
                  label: 'Approval Time', //TODO[Jyotishka]: add translation
                  values:
                    ticket.ticketInfo.approvalTimeInMinutes !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.approvalTimeInMinutes)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'Reassigned by', //TODO[Jyotishka]: add translation
                  values:
                    ticket.ticketInfo.reassignedByOpt !== undefined
                      ? TICKET_LIST_HELPER.mapToValuesList([getEmployeeDisplayName(ticket.ticketInfo.reassignedByOpt)])
                      : [],
                },
                {
                  size: 24,
                  label: 'tm_container_labels.MachineIssue',
                  values: ticket.ticketInfo.breakdownReasonDetails?.reason
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.breakdownReasonDetails?.reason])
                    : [],
                },
                {
                  size: 24,
                  label: 'table_model.IssueCategory',
                  values: ticket.ticketInfo.breakdownReasonDetails?.issueCategoryOpt
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.breakdownReasonDetails.issueCategoryOpt])
                    : [],
                },
                {
                  size: 24,
                  label: 'tm_container_labels.AdditionalInfo',
                  values: ticket.ticketInfo.description
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.description])
                    : [],
                },
                {
                  size: 24,
                  label: 'tm_container_labels.Location',
                  values: locationDisplayCallBack
                    ? TICKET_LIST_HELPER.getLocation(
                        locationDisplayCallBack(ticket.ticketInfo.ticketLocationDetails.location),
                        ticket.ticketInfo.ticketLocationDetails.workstationInfoOpt?.workstationName,
                      )
                    : [{ displayName: '--' }],
                  type: 'LOCATION',
                },
              ],
            },
          ],
          actions:
            selectedTicketStatus && tickets.length ? _getactionsInfoByStatus(selectedTicketStatus, actionConf) : [],
        };
      }),
    );
  },
};
